/**
 * Real client-facing portal renderer.
 *
 * Two modes, chosen by the `?preview=draft` query param:
 *   - Live (default): fetches GET /portal-config/live?tenantSlug=... on
 *     mount and renders published_config. This is what real end-customers
 *     see.
 *   - Preview (`?preview=draft`): renders nothing from the network at all —
 *     waits for `postMessage({ type: 'PORTAL_CONFIG', config, homeSections?,
 *     footerSections?, navbarSections? })` from the parent window (Portal
 *     Configuration page or the Design Library template editor) and
 *     re-renders on every message. This is the SAME component tree as live
 *     mode; only the data source differs, so a template can never visually
 *     drift from what a real customer would see.
 *
 * Section rendering follows the structured block-list palette from the
 * Design Library (Step 5): a fixed, named set of section types per
 * template kind — not a freeform HTML canvas.
 */
import { sanitizePortalHtml, bindPortalActions } from './sanitizePortalHtml.js'
import { useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { Link, NavLink, useLocation } from 'react-router'
import { api } from '../../api/client.js'
import { useClientAuthStore } from '../../store/clientAuthStore.js'
import { deriveTenantSlugFromHost } from '../../utils/deriveTenantSlugFromHost.js'
import PortalLayout from '../../components/client-portal/PortalLayout.js'
import ClientAuthNavWidget from '../../components/client-portal/ClientAuthNavWidget.js'
import type { PortalConfigData, PortalTemplateSection } from '@kc3/contracts'

export type { PortalConfigData, PortalTemplateSection }

export interface PortalTemplateLike {
  id: string
  type: 'home' | 'footer' | 'navbar'
  name: string
  sections: PortalTemplateSection[]
}

export interface LivePortalPayload {
  status: string
  config: PortalConfigData
  homeTemplate?: PortalTemplateLike | null
  footerTemplate?: PortalTemplateLike | null
  navbarTemplate?: PortalTemplateLike | null
}

export function visibleSorted(sections: PortalTemplateSection[]): PortalTemplateSection[] {
  return sections.filter((s) => s.visible).slice().sort((a, b) => a.order - b.order)
}

// design.theme sets a single portal-wide accent color (exposed as the
// --portal-accent CSS variable on the page root), used for the navbar
// background/active states, links, every button/CTA (all 3 starter
// templates read var(--portal-accent) directly), and any element that
// opts into it — not per-section colors, which stay independent (e.g.
// header_banner.background, navbar.textColor).
//
// design.theme historically stored one of these 4 named keys; the admin
// color picker now stores a literal hex/CSS color value instead. Both
// forms are supported here: a known named key resolves to its legacy
// hex value (existing tenants keep rendering identically), anything else
// is treated as a literal color and passed straight through.
const THEME_ACCENTS: Record<string, string> = {
  blue: '#3454cf',
  green: '#16a34a',
  purple: '#7c3aed',
  dark: '#111827',
}

export function themeAccent(theme: string): string {
  return THEME_ACCENTS[theme] ?? (theme || THEME_ACCENTS.blue!)
}

export interface ContactSocialInjection {
  contactPhone?: string
  facebookUrl?: string
  instagramUrl?: string
  linkedinUrl?: string
  twitterUrl?: string
  youtubeUrl?: string
  companyName?: string
  contactEmail?: string
  companyAddress?: string
  headerTitle?: string
  welcomeTitle?: string
  welcomeMessage?: string
  ticketButtonLabel?: string
  logoUrl?: string | null
  privacyUrl?: string
}

export function injectTenant(text: string, tenantSlug?: string, contact?: ContactSocialInjection): string {
  let result = text
  if (tenantSlug) {
    const upper = tenantSlug.toUpperCase()
    result = result.replace(/{tenantSlug}/g, tenantSlug).replace(/{TENANT_SLUG}/g, upper)
  }
  const logoUrl = contact?.logoUrl || ''
  const companyName = contact?.companyName || (tenantSlug ?? 'Our Company')
  const initial = companyName.trim().charAt(0).toUpperCase() || (tenantSlug ? tenantSlug.charAt(0).toUpperCase() : 'K')
  // PV-16: no tenant notice URL configured -> no link (previously fell back to a built-in page).
  const privacyUrl = contact?.privacyUrl ?? ''

  const logoImageTag = logoUrl
    ? `<img src="${logoUrl}" alt="${companyName}" class="h-8 max-h-8 w-auto object-contain" />`
    : `<div class="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white" style="background: var(--portal-accent);">${initial}</div>`

  result = result
    .replace(/{LOGO_URL}/g, logoUrl)
    .replace(/{LOGO_IMAGE}/g, logoImageTag)

  if (logoUrl) {
    // Replace default static placeholder logo blocks found in seeded footer templates
    result = result
      .replace(
        /<div class="flex h-8 w-8 items-center justify-center rounded-lg[^"]*"[^>]*>[A-Za-z0-9]<\/div>/g,
        logoImageTag,
      )
      .replace(
        /<div class="flex h-8 w-8 items-center justify-center rounded-lg[^>]*>[A-Za-z0-9]<\/div>/g,
        logoImageTag,
      )
      .replace(
        /<span class="flex h-9 w-9 items-center justify-center rounded-full"[^>]*>[\s\S]*?<\/span>/g,
        logoImageTag,
      )
  }

  // PV-16: a footer link whose target the tenant has not configured is removed rather than left
  // pointing at '#'. Bare tokens outside an anchor keep their old fallback.
  const escAttr = (v: string) => v.replace(/[&"'<>]/g, (c) => `&#${c.charCodeAt(0)};`)
  const dropUnsetAnchors = (token: string, value: string | undefined) => {
    if (value) return
    result = result.replace(new RegExp(`<a\\b[^>]*href="\\{${token}\\}"[^>]*>[\\s\\S]*?</a>`, 'g'), '')
  }
  dropUnsetAnchors('FACEBOOK_URL', contact?.facebookUrl)
  dropUnsetAnchors('INSTAGRAM_URL', contact?.instagramUrl)
  dropUnsetAnchors('LINKEDIN_URL', contact?.linkedinUrl)
  dropUnsetAnchors('TWITTER_URL', contact?.twitterUrl)
  dropUnsetAnchors('YOUTUBE_URL', contact?.youtubeUrl)
  dropUnsetAnchors('PRIVACY_URL', contact?.privacyUrl)
  // "Contact Us" placeholders that did nothing: link to the tenant's e-mail, or render as plain text.
  result = result.replace(/<a href="#" onclick="return false;"([^>]*)>([\s\S]*?)<\/a>/g, (_m, attrs: string, inner: string) =>
    contact?.contactEmail
      ? `<a href="mailto:${escAttr(contact.contactEmail)}"${attrs}>${inner}</a>`
      : `<span${attrs}>${inner}</span>`,
  )
  result = result
    .replace(/{FACEBOOK_URL}/g, contact?.facebookUrl || '#')
    .replace(/{INSTAGRAM_URL}/g, contact?.instagramUrl || '#')
    .replace(/{LINKEDIN_URL}/g, contact?.linkedinUrl || '#')
    .replace(/{TWITTER_URL}/g, contact?.twitterUrl || '#')
    .replace(/{YOUTUBE_URL}/g, contact?.youtubeUrl || '#')
    .replace(/{CONTACT_PHONE}/g, contact?.contactPhone || 'Contact us for phone support')
    .replace(/{COMPANY_NAME}/g, companyName)
    .replace(/{CONTACT_EMAIL}/g, contact?.contactEmail || 'support@example.com')
    .replace(/{COMPANY_ADDRESS}/g, contact?.companyAddress || '')
    .replace(/{HEADER_TITLE}/g, contact?.headerTitle || 'Help Center')
    .replace(/{WELCOME_TITLE}/g, contact?.welcomeTitle || 'Hello, how can we help you today?')
    .replace(/{WELCOME_MESSAGE}/g, contact?.welcomeMessage || 'Browse our knowledge base, read the FAQ, or open a support conversation.')
    .replace(/{TICKET_BUTTON_LABEL}/g, contact?.ticketButtonLabel || 'Raise a Conversation')
    .replace(/{PRIVACY_URL}/g, escAttr(privacyUrl))
    .replace(/{PRIVACY_POLICY_LINK}/g, privacyUrl ? `<a href="${escAttr(privacyUrl)}" class="hover:underline">Privacy Notice</a>` : '')
  return result
}

// Machine keys chosen in the Design Library's category_tiles picker,
// mapped to display labels — matches the content types accessPermissions
// already distinguishes (news/knowledgebase/faq/announcements).
const CATEGORY_TILE_LABELS: Record<string, string> = {
  knowledgebase: 'Knowledgebase',
  faq: 'FAQ',
  news: 'News',
  announcements: 'Announcements',
}

const CATEGORY_TILE_ICONS: Record<string, string> = {
  knowledgebase: 'pi pi-book',
  faq: 'pi pi-question-circle',
  news: 'pi pi-megaphone',
  announcements: 'pi pi-bell',
}

const CATEGORY_TILE_DESCRIPTIONS: Record<string, string> = {
  knowledgebase: 'Guides and how-tos',
  faq: 'Common questions answered',
  news: 'Latest updates',
  announcements: 'Important notices',
}

// Machine keys chosen in the Design Library's navbar links picker, mapped
// to display labels. 'home' has no dedicated content type in
// accessPermissions (it's the landing page itself), so it isn't in
// CATEGORY_TILE_LABELS above.
const NAVBAR_LINK_LABELS: Record<string, string> = {
  home: 'Home',
  knowledgebase: 'Knowledge Base',
  faq: 'FAQ',
  news: 'News',
  announcements: 'Announcements',
  submit_ticket: 'Submit Conversation',
  my_tickets: 'My Conversations',
  login: 'Login',
  register: 'Register',
}

// Content types whose visibility can be gated to signed-in visitors via
// accessPermissions.<key>.visibility — mirrors CONTENT_TYPE_MAP in
// apps/api/src/routes/portal/content.ts.
type VisibilityGatedKey = 'news' | 'knowledgebase' | 'faq' | 'announcements'
const VISIBILITY_GATED_KEYS = new Set<string>(['news', 'knowledgebase', 'faq', 'announcements'])

// Mirrors the server-side gates (portal/content.ts visibility, tickets.ts
// submitTicket.who): links that would dead-end an anonymous visitor on a
// permission wall are hidden instead of rendered. Used by the structured
// navbar, custom_html navbars, and category tiles alike.
function isPortalLinkGated(href: string, config: PortalConfigData, isAuthenticated: boolean): boolean {
  if (isAuthenticated) return false
  const contentMatch = href.match(/^\/client-portal\/content\/([^/?#]+)/)
  if (contentMatch && VISIBILITY_GATED_KEYS.has(contentMatch[1]!)) {
    return config.accessPermissions[contentMatch[1] as VisibilityGatedKey].visibility === 'logged_in_user'
  }
  if (href === '/client-portal/tickets/new') {
    return config.clientPermissions.submitTicket.who === 'logged_in_user'
  }
  // 'my_tickets' — same rule the structured navbar applies: conversations
  // require a session, so anonymous visitors never see the link.
  if (href === '/client-portal/tickets') return true
  return false
}

// Resolves a category_tiles / navbar link key to a real route under this
// tenant's portal. Content-type keys (knowledgebase/faq/news/announcements)
// go to the category list; 'home' goes to the portal root. submit_ticket/
// my_tickets have no page built yet (no portal visitor auth exists), so
// they resolve to `null` and render as inert text instead of a dead link.
function linkHrefFor(tenantSlug: string | undefined, key: string): string | null {
  if (!tenantSlug) return null
  if (key === 'home') return '/client-portal'
  if (key === 'login') return '/client-portal/login'
  if (key === 'register') return '/client-portal/register'
  if (key === 'submit_ticket') return '/client-portal/tickets/new'
  if (key === 'my_tickets') return '/client-portal/tickets'
  if (key in CATEGORY_TILE_LABELS) return `/client-portal/content/${key}`
  return null
}

interface FeaturedArticle {
  id: string
  title: string
  slug: string
  excerpt: string
  isPinned: boolean
  contentType: string
  categorySlug: string
}

interface PortalFormNavItem {
  id: string
  name: string
  /** Admin-set tab label — falls back to name when null. */
  navLabel: string | null
  slug: string
}

/* ─── dynamic portal forms — the admin picks forms in Portal Configuration
   → Portal Forms (config.forms.selectedFormIds); this resolves them to
   {slug,name} nav items appended after the template's configured links.
   Renders nothing in draft preview (no tenant to fetch against) and
   filters to ACTIVE forms only, so deactivating a form drops it from the
   navbar on the next publish without touching the selection. ─── */
function usePortalFormsNav(config: PortalConfigData, tenantSlug: string | undefined): PortalFormNavItem[] {
  const [available, setAvailable] = useState<PortalFormNavItem[]>([])
  const selectedIds = config.forms?.selectedFormIds ?? []

  useEffect(() => {
    if (!tenantSlug || selectedIds.length === 0) return
    let cancelled = false
    api
      .get<{ forms: PortalFormNavItem[] }>(`/portal/${tenantSlug}/forms`)
      .then((res) => {
        if (!cancelled) setAvailable(res.forms ?? [])
      })
      .catch(() => {
        if (!cancelled) setAvailable([])
      })
    return () => {
      cancelled = true
    }
  }, [tenantSlug, selectedIds.length])

  return useMemo(() => {
    const byId = new Map(available.map((f) => [f.id, f] as const))
    return selectedIds.map((fid) => byId.get(fid)).filter((f): f is PortalFormNavItem => Boolean(f))
  }, [available, selectedIds])
}

function FormNavItems({
  config,
  tenantSlug,
  onNavigate,
}: {
  config: PortalConfigData
  tenantSlug?: string | undefined
  onNavigate?: () => void
}): JSX.Element | null {
  const ordered = usePortalFormsNav(config, tenantSlug)
  if (ordered.length === 0) return null
  return (
    <>
      {ordered.map((form) => (
        <li key={form.id}>
          <Link to={`/client-portal/forms/${form.slug}`} className="hover:opacity-75" style={{ color: 'inherit' }} onClick={onNavigate}>
            {form.navLabel || form.name}
          </Link>
        </li>
      ))}
    </>
  )
}

/* Anchor variant for custom_html navbars — real <a> tags portaled into the
   template's own markup so its element-level CSS (nav a {…}) applies;
   .kc3-portal-form-link is the styling hook for template authors. NavLink
   applies kc3-nav-active itself: the DOM-scanning effect in
   CustomHtmlWithAuthSlot runs before these portal children mount (the
   forms list resolves asynchronously), so it can never mark them. */
function FormNavLinks({
  config,
  tenantSlug,
}: {
  config: PortalConfigData
  tenantSlug?: string | undefined
}): JSX.Element | null {
  const ordered = usePortalFormsNav(config, tenantSlug)
  if (ordered.length === 0) return null
  return (
    <>
      {ordered.map((form) => (
        <NavLink
          key={form.id}
          to={`/client-portal/forms/${form.slug}`}
          end
          className={({ isActive }) => `kc3-portal-form-link${isActive ? ' kc3-nav-active' : ''}`}
          style={{ color: 'inherit', textDecoration: 'none' }}
        >
          {form.navLabel || form.name}
        </NavLink>
      ))}
    </>
  )
}

/* ─── home: featured/recent articles — fetches the most recently published
   articles across every visible content type. In preview mode (tenantSlug
   is literally the string "preview", not a real tenant — see
   SearchBarSection's comment above) it shows a placeholder instead of
   fetching against a nonexistent tenant. ─── */
function FeaturedArticlesSection({ title, tenantSlug, isPreview }: { title: string; tenantSlug?: string; isPreview: boolean }): JSX.Element {
  const [articles, setArticles] = useState<FeaturedArticle[]>([])
  const [loaded, setLoaded] = useState(false)
  const active = !isPreview && !!tenantSlug

  useEffect(() => {
    if (!active) return
    let cancelled = false
    api
      .get<{ articles: FeaturedArticle[] }>(`/portal/${tenantSlug}/content/featured?limit=6`)
      .then((res) => {
        if (!cancelled) {
          setArticles(res.articles)
          setLoaded(true)
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [active, tenantSlug])

  return (
    <section className="py-8 px-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <div className="h-0.5 flex-1 ml-4 rounded-[3px]" style={{ backgroundColor: 'color-mix(in srgb, var(--portal-accent) 15%, transparent)' }} />
      </div>
      {!active && <div className="text-[13px] text-gray-400">Recent articles will appear here on the live portal.</div>}
      {active && !loaded && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1].map((i) => (
            <div key={i} className="p-4 border border-gray-100 rounded-[3px] bg-gray-50 animate-pulse h-24" />
          ))}
        </div>
      )}
      {active && loaded && articles.length === 0 && <div className="text-[13px] text-gray-400">No published articles yet.</div>}
      {active && articles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/client-portal/content/${a.contentType}/${a.categorySlug}/${a.slug}`}
              className="group block p-4 border border-gray-200 rounded-[3px] bg-white transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-transparent"
            >
              <div className="flex items-center gap-2">
                {a.isPinned && <span className="text-xs" title="Pinned">📌</span>}
                <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--portal-accent)' }}>
                  {CATEGORY_TILE_LABELS[a.contentType] ?? a.contentType}
                </span>
              </div>
              <p className="font-medium text-gray-900 mt-1.5 group-hover:text-[var(--portal-accent)] transition-colors">{a.title}</p>
              {a.excerpt && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{a.excerpt}</p>}
            </Link>
          ))}
        </div>
      )}
    </section>
  )
}

// Marker rendered where a custom_html template wants dynamic portal-form
// links — `{PORTAL_FORM_LINKS}` in the template HTML expands to this span,
// then FormNavLinks is portaled in below (same pattern as
// #client-auth-nav-slot). display:contents keeps the marker layout-neutral.
const PORTAL_FORMS_SLOT_CLASS = 'kc3-portal-forms-nav-slot'
const PORTAL_FORMS_SLOT_HTML = `<span class="${PORTAL_FORMS_SLOT_CLASS}" style="display:contents"></span>`

// SE-20: tenant-authored HTML rendered without the nav/auth slots.
function SanitizedPortalHtml({
  html,
  className,
  style,
}: {
  html: string
  className: string
  style?: React.CSSProperties
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const htmlProp = useMemo(() => ({ __html: sanitizePortalHtml(html) }), [html])
  useEffect(() => {
    const el = ref.current
    return el ? bindPortalActions(el) : undefined
  }, [])
  return <div ref={ref} className={className} style={style} dangerouslySetInnerHTML={htmlProp} />
}

// Blue Hero and Template 3's navbars are custom_html (raw HTML strings, no
// direct React access) — their markup reserves a `#client-auth-nav-slot` div
// where the shared login/register/profile widget gets portaled in once the
// HTML is in the DOM. No-ops (renders nothing) for any custom_html section
// that doesn't contain that slot, e.g. home-page custom_html sections.
// `inNavbar` additionally enables portal-form links: an explicit
// `{PORTAL_FORM_LINKS}` marker wins, otherwise the links are appended inside
// the first </nav> so unedited custom navbars still surface selected forms.
function CustomHtmlWithAuthSlot({
  html,
  className,
  config,
  tenantSlug,
  inNavbar = false,
}: {
  html: string
  className: string
  config: PortalConfigData
  tenantSlug?: string | undefined
  inNavbar?: boolean
}): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const [slotNode, setSlotNode] = useState<Element | null>(null)
  const [formSlotNodes, setFormSlotNodes] = useState<Element[]>([])
  const location = useLocation()
  const isAuthenticated = useClientAuthStore((s) => s.user) !== null

  const resolvedHtml = useMemo(() => {
    let next = html
    const hasMarker = next.includes('{PORTAL_FORM_LINKS}')
    next = next.replaceAll('{PORTAL_FORM_LINKS}', PORTAL_FORMS_SLOT_HTML)
    if (inNavbar && !hasMarker) {
      // No explicit marker — append the slot inside the first </nav> so
      // custom navbars that predate this feature still show selected forms.
      next = next.replace(/<\/nav>/i, `${PORTAL_FORMS_SLOT_HTML}</nav>`)
    }
    // SE-20: tenant-authored markup is sanitized before it reaches innerHTML.
    return sanitizePortalHtml(next)
  }, [html, inNavbar])

  // dangerouslySetInnerHTML requires a NEW object identity to skip re-assigning
  // innerHTML on a render where `html`'s string content hasn't changed — an
  // inline `{ __html: html }` literal is a fresh object every render, which
  // makes React reset innerHTML (destroying the #client-auth-nav-slot node)
  // on every re-render of this component, orphaning the portal mounted into
  // it below. Memoizing on the string content keeps the DOM subtree (and the
  // slot node the portal targets) stable across renders that don't actually
  // change the HTML.
  const htmlProp = useMemo(() => ({ __html: resolvedHtml }), [resolvedHtml])

  useEffect(() => {
    const el = containerRef.current
    return el ? bindPortalActions(el) : undefined
  }, [])

  useEffect(() => {
    setSlotNode(containerRef.current?.querySelector('#client-auth-nav-slot') ?? null)
    setFormSlotNodes(Array.from(containerRef.current?.querySelectorAll(`.${PORTAL_FORMS_SLOT_CLASS}`) ?? []))
  }, [resolvedHtml])

  // The Blue Hero / Sidebar Console navbars are raw HTML with no React
  // router access, so their nav links can't know which page is current on
  // their own — mark the one matching the current path with a shared
  // `kc3-nav-active` class whenever the route changes (see index.css for
  // what that class looks like per template), instead of hardcoding a
  // permanent "active" look on Home that stayed lit on every other page too.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const links = container.querySelectorAll<HTMLAnchorElement>('a[href]')
    links.forEach((link) => {
      const href = link.getAttribute('href') ?? ''
      link.classList.toggle('kc3-nav-active', href === location.pathname)
      // Links that would dead-end on a permission wall are hidden rather
      // than rendered — mirrors the visibleLinks filter in the structured
      // navbar. The wrapping <li> is hidden too where the template uses one,
      // so no empty bullets remain.
      const target = (link.closest('li') ?? link) as HTMLElement
      target.style.display = isPortalLinkGated(href, config, isAuthenticated) ? 'none' : ''
    })
  }, [resolvedHtml, location.pathname, config, isAuthenticated])

  return (
    <>
      <div ref={containerRef} className={className} dangerouslySetInnerHTML={htmlProp} />
      {slotNode && createPortal(
        <ClientAuthNavWidget loginHref="/client-portal/login" registerHref="/client-portal/register" />,
        slotNode,
      )}
      {inNavbar &&
        formSlotNodes.map((node, i) =>
          createPortal(<FormNavLinks config={config} tenantSlug={tenantSlug} />, node, i),
        )}
    </>
  )
}

/* ─── home section palette ─── */
export function HomeSection({
  section,
  config,
  tenantSlug,
  isPreview,
  inNavbar = false,
}: {
  section: PortalTemplateSection
  config: PortalConfigData
  tenantSlug?: string
  isPreview: boolean
  /** True when this section renders as part of the navbar template —
      enables portal-form link injection for custom_html navbars. */
  inNavbar?: boolean
}): JSX.Element | null {
  const settings = section.settings
  switch (section.sectionType) {
    case 'navbar': {
      const rawLinks = (settings.links as string[] | undefined) ?? ['home', 'knowledgebase', 'faq', 'login', 'register']
      const navbarLinks = [...new Set([...rawLinks, 'login', 'register'])].filter((link) => link !== 'create_ticket')
      const brandText = injectTenant((settings.brandText as string) || config.design.headerTitle || 'Portal', tenantSlug, config.design)
      const logoUrl = (settings.logoUrl as string) || config.design.logoUrl
      const backgroundColor = (settings.backgroundColor as string) || 'var(--portal-accent)'
      const textColor = (settings.textColor as string) || '#ffffff'
      const isSidebar = config.design.layout === 'menu_side'
      const isAuthenticated = useClientAuthStore((s) => s.user) !== null
      const visibleLinks = navbarLinks.filter((link) => {
        if (link === 'login' || link === 'register') return false
        if (link === 'my_tickets') return isAuthenticated
        const href = linkHrefFor(tenantSlug, link)
        if (href && isPortalLinkGated(href, config, isAuthenticated)) return false
        return true
      })
      const ctaLink = settings.ctaLink as { key: string; label?: string } | undefined
      const [mobileOpen, setMobileOpen] = useState(false)

      const renderLink = (link: string): JSX.Element | null => {
        const href = linkHrefFor(tenantSlug, link)
        return (
          <li key={link}>
            {href ? (
              <Link to={href} className="hover:opacity-75" style={{ color: 'inherit' }} onClick={() => setMobileOpen(false)}>
                {NAVBAR_LINK_LABELS[link] ?? link}
              </Link>
            ) : (
              <span className="cursor-default opacity-90">{NAVBAR_LINK_LABELS[link] ?? link}</span>
            )}
          </li>
        )
      }

      const ctaHref = ctaLink ? linkHrefFor(tenantSlug, ctaLink.key) : null
      const ctaButton = ctaLink && ctaHref && !isPortalLinkGated(ctaHref, config, isAuthenticated) && (
        <Link
          to={ctaHref}
          onClick={() => setMobileOpen(false)}
          className="inline-flex items-center rounded-[3px] px-4 py-2 text-[13px] font-semibold shadow-sm hover:brightness-110 transition-all"
          style={{ backgroundColor: 'var(--portal-accent)', color: 'var(--portal-accent-text, #ffffff)' }}
        >
          {injectTenant(ctaLink.label ?? NAVBAR_LINK_LABELS[ctaLink.key] ?? ctaLink.key, tenantSlug, config.design)}
        </Link>
      )

      const authWidget = (
        <ClientAuthNavWidget loginHref="/client-portal/login" registerHref="/client-portal/register" />
      )

      if (isSidebar) {
        return (
          <nav className="flex flex-col gap-6 px-4 py-6 h-full" style={{ backgroundColor, color: textColor }}>
            <Link to="/client-portal" className="flex items-center gap-2 font-semibold text-base hover:opacity-90" style={{ color: 'inherit' }}>
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-7" />
              ) : (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] text-xs font-bold"
                  style={{ backgroundColor: 'var(--portal-accent)', color: 'var(--portal-accent-text, #ffffff)' }}
                  aria-hidden="true"
                >
                  {brandText.trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
              <span>{brandText}</span>
            </Link>
            <ul className="flex flex-col gap-3 text-sm">
              {visibleLinks.map((link) => renderLink(link))}
              <FormNavItems config={config} tenantSlug={tenantSlug} onNavigate={() => setMobileOpen(false)} />
            </ul>
            {ctaButton}
            {authWidget}
          </nav>
        )
      }

      return (
        <nav
          className="shadow-sm sticky top-0 z-10"
          style={{ backgroundColor, color: textColor }}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 py-3">
            <Link to="/client-portal" className="flex items-center gap-2 font-semibold text-base hover:opacity-90" style={{ color: 'inherit' }}>
              {logoUrl ? (
                <img src={logoUrl} alt="" className="h-7" />
              ) : (
                <span
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[3px] text-xs font-bold"
                  style={{ backgroundColor: 'var(--portal-accent)', color: 'var(--portal-accent-text, #ffffff)' }}
                  aria-hidden="true"
                >
                  {brandText.trim().charAt(0).toUpperCase() || '?'}
                </span>
              )}
              <span>{brandText}</span>
            </Link>

            <ul className="hidden md:flex items-center gap-6 text-sm font-medium">
              {visibleLinks.map((link) => renderLink(link))}
              <FormNavItems config={config} tenantSlug={tenantSlug} />
            </ul>
            <div className="hidden md:flex items-center gap-4">
              {ctaButton}
              {authWidget}
            </div>

            <button
              type="button"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((v) => !v)}
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-[3px] hover:opacity-75"
              style={{ color: 'inherit' }}
            >
              <i className={mobileOpen ? 'pi pi-times text-lg' : 'pi pi-bars text-lg'} />
            </button>
          </div>

          {mobileOpen && (
            <div
              className="md:hidden absolute top-full left-0 right-0 shadow-md px-4 sm:px-6 py-4 flex flex-col gap-4"
              style={{ backgroundColor, color: textColor }}
            >
              <ul className="flex flex-col gap-3 text-sm font-medium">
                {visibleLinks.map((link) => renderLink(link))}
                <FormNavItems config={config} tenantSlug={tenantSlug} onNavigate={() => setMobileOpen(false)} />
              </ul>
              <div className="flex items-center gap-4">
                {ctaButton}
                {authWidget}
              </div>
            </div>
          )}
        </nav>
      )
    }
    case 'header_banner': {
      const background = (settings.backgroundColor as string) || config.design.background.color || undefined
      const tag = injectTenant((settings.tag as string) || '', tenantSlug)
      const align = (settings.align as 'left' | 'center' | undefined) || 'center'
      const textColor = (settings.textColor as string) || '#111827'
      const imageUrl = (settings.imageUrl as string) || undefined
      const title = injectTenant(
        (settings.title as string) || config.design.welcomeTitle || config.design.headerTitle || 'Welcome',
        tenantSlug
      )
      const message = injectTenant((settings.message as string) || config.design.welcomeMessage || '', tenantSlug)
      const isLeft = align === 'left'
      return (
        <section className="py-20 px-4 relative" style={{ background, color: textColor }}>
          <div
            className={`max-w-6xl mx-auto flex flex-col ${
              isLeft && imageUrl ? 'md:flex-row md:items-center md:justify-between' : ''
            } ${isLeft ? 'items-start text-left' : 'items-center text-center'}`}
          >
            <div className={`${imageUrl ? 'max-w-2xl' : ''}`}>
              {tag && (
                <span className="mb-4 inline-flex items-center rounded-[3px] bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-gray-700 shadow-sm">
                  {tag}
                </span>
              )}
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">{title}</h1>
              {message && <p className="mt-4 text-lg opacity-90 max-w-2xl leading-relaxed">{message}</p>}
            </div>
            {imageUrl && <img src={imageUrl} alt="" className="mt-8 md:mt-0 w-full max-w-md object-contain" />}
          </div>
        </section>
      )
    }
    case 'quick_actions': {
      interface QuickAction {
        label: string
        icon?: string
        link?: string
        variant?: 'primary' | 'secondary'
      }
      const actions = (settings.actions as QuickAction[] | undefined) ?? []
      return (
        <section className="py-2 px-4 max-w-5xl mx-auto">
          <div className="flex flex-wrap items-center gap-3">
            {actions.map((a, i) => {
              const content = (
                <span
                  className={`inline-flex items-center gap-2 rounded-[3px] px-5 py-2.5 text-[13px] font-semibold transition-all ${
                    a.variant === 'secondary'
                      ? 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                      : 'shadow-md hover:brightness-110'
                  }`}
                  style={a.variant === 'secondary' ? undefined : { backgroundColor: 'var(--portal-accent)', color: 'var(--portal-accent-text, #ffffff)' }}
                >
                  {a.icon && <i className={a.icon} />}
                  {a.label}
                </span>
              )
              if (!a.link || a.link === '#') return <div key={i}>{content}</div>
              return a.link.startsWith('/') ? (
                <Link key={i} to={a.link}>
                  {content}
                </Link>
              ) : (
                <a key={i} href={a.link} className="inline-flex">
                  {content}
                </a>
              )
            })}
          </div>
        </section>
      )
    }
    case 'welcome_message':
      return (
        <section className="py-10 px-4 text-center max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold mb-3 text-gray-900">{config.design.welcomeTitle || (settings.title as string) || ''}</h2>
          <p className="text-gray-500 leading-relaxed">{config.design.welcomeMessage || (settings.message as string) || ''}</p>
        </section>
      )
    case 'category_tiles': {
      interface TileItem {
        icon?: string
        title: string
        description?: string
        link?: string
      }
      const isAuthenticatedTiles = useClientAuthStore((s) => s.user) !== null
      const rawTiles = (settings.tiles as TileItem[] | undefined) ?? []
      const legacyTiles = (settings.categories as string[] | undefined) ?? []
      const tiles: TileItem[] = (
        rawTiles.length > 0
          ? rawTiles
          : legacyTiles.map((c) => ({
              icon: CATEGORY_TILE_ICONS[c] ?? 'pi pi-folder',
              title: CATEGORY_TILE_LABELS[c] ?? c,
              description: CATEGORY_TILE_DESCRIPTIONS[c] ?? '',
              link: linkHrefFor(tenantSlug, c) || '',
            }))
      ).filter((t) => !(t.link && isPortalLinkGated(t.link, config, isAuthenticatedTiles)))
      return (
        <section className="py-10 px-4 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {tiles.map((t, idx) => {
              const inner = (
                <div className="group h-full p-6 rounded-[3px] border border-gray-200 bg-white shadow-sm transition-all hover:shadow-lg hover:-translate-y-0.5 hover:border-transparent">
                  <div
                    className="h-12 w-12 rounded-[3px] flex items-center justify-center mb-4 transition-colors"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--portal-accent) 10%, transparent)' }}
                  >
                    <i className={`${t.icon || 'pi pi-folder'} text-xl`} style={{ color: 'var(--portal-accent)' }} />
                  </div>
                  <h3 className="text-[13px] font-semibold text-gray-900">{t.title}</h3>
                  {t.description && <p className="text-[12px] text-gray-500 mt-1.5 leading-relaxed">{t.description}</p>}
                </div>
              )
              return t.link ? (
                <Link key={`${t.title}-${idx}`} to={t.link}>
                  {inner}
                </Link>
              ) : (
                <div key={`${t.title}-${idx}`}>{inner}</div>
              )
            })}
          </div>
        </section>
      )
    }
    case 'featured_articles':
      return <FeaturedArticlesSection title={(settings.title as string) || 'Featured Articles'} {...(tenantSlug !== undefined && { tenantSlug })} isPreview={isPreview} />
    case 'announcement_ribbon':
      return (
        <section
          className="py-3 px-4 text-center text-[13px] font-medium flex items-center justify-center gap-2"
          style={{
            backgroundColor: (settings.backgroundColor as string) || 'color-mix(in srgb, var(--portal-accent) 12%, transparent)',
            color: (settings.textColor as string) || 'var(--portal-accent)',
          }}
        >
          <i className="pi pi-megaphone text-xs" />
          {injectTenant((settings.text as string) || 'Announcements appear here.', tenantSlug)}
        </section>
      )
    case 'custom_html': {
      const fullWidth = (settings.fullWidth as boolean) ?? false
      return (
        <CustomHtmlWithAuthSlot
          className={fullWidth ? 'w-full' : 'py-6 px-4 max-w-4xl mx-auto'}
          html={injectTenant((settings.html as string) || '', tenantSlug, config.design)}
          config={config}
          tenantSlug={tenantSlug}
          inNavbar={inNavbar}
        />
      )
    }
    default:
      return null
  }
}

// Machine keys chosen in the Design Library's social_icons picker, mapped to
// a PrimeIcons class — matches SOCIAL_NETWORK_OPTIONS in sectionPalette.tsx.
const SOCIAL_ICON_CLASSES: Record<string, string> = {
  facebook: 'pi pi-facebook',
  twitter: 'pi pi-twitter',
  linkedin: 'pi pi-linkedin',
  instagram: 'pi pi-instagram',
  youtube: 'pi pi-youtube',
  github: 'pi pi-github',
}

interface FooterLinkColumn {
  title: string
  links: { label: string; url: string }[]
}

interface FooterSocialLink {
  network: string
  url: string
}

/* ─── footer section palette ─── */
export function FooterSection({ section, textColor: footerTextColor, tenantSlug, design }: { section: PortalTemplateSection; textColor: string; tenantSlug?: string; design?: ContactSocialInjection }): JSX.Element | null {
  const settings = section.settings
  // Each footer section can set its own background/text color, layered on
  // top of the footer container's own colors (settings.backgroundColor /
  // settings.textColor, e.g. '' | '#f3f4f6') — undefined/'' inherits the
  // footer container's color.
  const backgroundColor = (settings.backgroundColor as string) || undefined
  const sectionStyle = backgroundColor ? { backgroundColor } : undefined
  const textColor = (settings.textColor as string) || footerTextColor
  switch (section.sectionType) {
    case 'link_columns':
      return (
        <div style={sectionStyle} className={backgroundColor ? '-mx-4 px-4' : undefined}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 py-8">
            {((settings.columns as FooterLinkColumn[] | undefined) ?? []).map((col, i) => (
              <div key={i}>
                <h4 className="text-[13px] font-semibold mb-3" style={{ color: textColor }}>{col.title || 'Untitled'}</h4>
                <ul className="space-y-2 text-[13px]">
                  {col.links.map((link, j) => (
                    <li key={j}>
                      {link.url ? (
                        <a href={link.url} className="opacity-70 hover:opacity-100 transition-opacity" style={{ color: textColor }}>
                          {link.label || link.url}
                        </a>
                      ) : (
                        <span className="opacity-70" style={{ color: textColor }}>{link.label || 'Untitled link'}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )
    case 'social_icons': {
      const links = ((settings.links as FooterSocialLink[] | undefined) ?? []).filter((link) => link.url)
      if (links.length === 0) return null
      const iconColor = (settings.textColor as string) || 'var(--portal-accent)'
      return (
        <div style={sectionStyle} className={backgroundColor ? '-mx-4 px-4' : undefined}>
          <div className="flex gap-4 justify-center py-6">
            {links.map((link) => (
              <a
                key={link.network}
                href={link.url}
                className="w-9 h-9 rounded-[3px] border flex items-center justify-center opacity-70 hover:opacity-100 hover:border-current transition-opacity"
                style={{ color: iconColor, borderColor: iconColor }}
                aria-label={link.network}
              >
                <i className={SOCIAL_ICON_CLASSES[link.network] ?? 'pi pi-globe'} />
              </a>
            ))}
          </div>
        </div>
      )
    }
    case 'copyright_text':
      return (
        <div style={sectionStyle} className={`text-center text-[13px] opacity-70 py-4 ${backgroundColor ? '-mx-4 px-4' : ''}`}>
          <span style={{ color: textColor }}>{injectTenant((settings.text as string) || `© ${new Date().getFullYear()}`, tenantSlug)}</span>
        </div>
      )
    case 'custom_html': {
      const fullWidth = (settings.fullWidth as boolean) ?? false
      return (
        <SanitizedPortalHtml
          style={fullWidth ? { ...sectionStyle } : { color: textColor, ...sectionStyle }}
          className={fullWidth ? 'w-full' : `py-4 ${backgroundColor ? '-mx-4 px-4' : ''}`}
          html={injectTenant((settings.html as string) || '', tenantSlug, design)}
        />
      )
    }
    default:
      return null
  }
}

export function PortalHomeContent({
  homeSections,
  config,
  tenantSlug,
  isPreview,
}: {
  homeSections: PortalTemplateSection[]
  config: PortalConfigData
  tenantSlug?: string
  isPreview: boolean
}): JSX.Element {
  return (
    <>
      {visibleSorted(homeSections).map((section) => (
        <HomeSection key={`${section.sectionType}-${section.order}`} section={section} config={config} {...(tenantSlug !== undefined && { tenantSlug })} isPreview={isPreview} />
      ))}
      {homeSections.length === 0 && (
        <div className="p-10 text-center text-gray-400 text-sm">No home template selected.</div>
      )}
    </>
  )
}

export function PortalHomeRoute(): JSX.Element {
  const tenantSlug = deriveTenantSlugFromHost(window.location.host) ?? undefined
  return (
    <PortalLayout>
      {(ctx) => <PortalHomeContent homeSections={ctx.homeSections} config={ctx.config} {...(tenantSlug !== undefined && { tenantSlug })} isPreview={false} />}
    </PortalLayout>
  )
}

const DEFAULT_CONFIG: PortalConfigData = {
  design: {
    templateMode: 'starter',
    headerTitle: '',
    welcomeTitle: '',
    welcomeMessage: '',
    theme: 'blue',
    buttonTextColor: '#ffffff',
    ticketButtonLabel: 'Raise a Conversation',
    layout: 'menu_top',
    homeDesign: null,
    footerDesign: null,
    navbarDesign: null,
    font: 'Inter',
    background: { imageUrl: null, color: '#FFFFFF', opacity: 1, preset: null },
    logoUrl: null,
    faviconUrl: null,
    bannerUrl: null,
    footerBackgroundColor: '',
    footerTextColor: '#6b7280',
    companyName: '',
    contactEmail: '',
    companyAddress: '',
    contactPhone: '',
    facebookUrl: '',
    instagramUrl: '',
    linkedinUrl: '',
    twitterUrl: '',
    youtubeUrl: '',
  },
  accessPermissions: {
    news: { visibility: 'logged_in_user' },
    knowledgebase: { visibility: 'logged_in_user' },
    faq: { visibility: 'logged_in_user' },
    announcements: { visibility: 'logged_in_user' },
    chat: { enabled: true, botMode: 'faq_auto' },
    call: { enabled: false },
    googleSignin: { enabled: false, clientId: '', clientSecret: '' },
    microsoftSignin: { enabled: false, clientId: '', clientSecret: '' },
    facebookSignin: { enabled: false, appId: '', appSecret: '' },
    approvalLogin: false,
    forgotPassword: true,
  },
  clientPermissions: {
    downloadAttachments: true,
    deleteAttachments: false,
    attachmentViewNewTab: true,
    submitTicket: { who: 'everyone' },
    viewTicket: { who: 'logged_in_user' },
  },
  ticketRules: {
    reopenDays: 7,
    reopenMessage: '',
    disclaimer: { enabled: false, message: '' },
    ticketFormId: null,
    privacyPolicy: '',
    formFields: {
      showConversationForm: true,
      showConsumerType: true,
      showSubject: true,
      showDescription: true,
      showAttachments: true,
      showLocation: true,
    },
  },
  rating: {
    perReplyEnabled: false,
    perInteractionEnabled: true,
    ratingConfig: { scale: 5, labels: {} },
  },
  forms: { selectedFormIds: [] },
  chatWidget: {
    enabled: false,
    allowedDomains: [],
    position: 'bottom_right',
    offsetBottom: 24,
    offsetRight: 24,
    iconSize: 56,
    primaryColor: '#3454cf',
    welcomeMessage: 'Hi there! How can we help you today?',
    showUnreadCount: true,
  },
  voiceAssistant: {
    enabled: false,
    agentId: null,
    label: 'Talk to our AI assistant',
    position: 'bottom_left',
    color: '#3366FF',
    requireLogin: false,
  },
  privacyNotice: {
    enabled: false,
    noticeUrl: '',
    noticeText: '',
    version: '',
    minimumAge: 0,
    translations: {},
  },
}

export default function PortalPage(): JSX.Element {
  const tenantSlug = deriveTenantSlugFromHost(window.location.host) ?? undefined
  const [previewConfig, setPreviewConfig] = useState<PortalConfigData>(DEFAULT_CONFIG)
  const [previewHomeSections, setPreviewHomeSections] = useState<PortalTemplateSection[]>([])
  const [previewFooterTemplate, setPreviewFooterTemplate] = useState<PortalTemplateLike | null>(null)
  const [previewNavbarTemplate, setPreviewNavbarTemplate] = useState<PortalTemplateLike | null>(null)
  const [previewLoaded, setPreviewLoaded] = useState(false)
  const [previewChatSession, setPreviewChatSession] = useState<{ token: string; visitorId: string } | null>(null)

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type !== 'PORTAL_CONFIG') return
      if (event.data.config) setPreviewConfig(event.data.config as PortalConfigData)
      if (event.data.homeSections) setPreviewHomeSections(event.data.homeSections as PortalTemplateSection[])
      if (event.data.footerSections) {
        setPreviewFooterTemplate({ id: 'preview-footer', type: 'footer', name: 'preview', sections: event.data.footerSections as PortalTemplateSection[] })
      }
      if (event.data.navbarSections) {
        setPreviewNavbarTemplate({ id: 'preview-navbar', type: 'navbar', name: 'preview', sections: event.data.navbarSections as PortalTemplateSection[] })
      }
      setPreviewChatSession((event.data.chatSession as { token: string; visitorId: string } | null) ?? null)
      setPreviewLoaded(true)
    }
    window.addEventListener('message', handler)
    window.parent.postMessage({ type: 'PORTAL_READY' }, '*')
    return () => window.removeEventListener('message', handler)
  }, [])

  if (!previewLoaded) {
    return <div className="p-10 text-center text-gray-400 text-sm">Waiting for preview data…</div>
  }

  return (
    <PortalLayout
      isPreview
      overrideConfig={previewConfig}
      overrideTemplates={{ navbar: previewNavbarTemplate, footer: previewFooterTemplate }}
      overrideChatSession={previewChatSession}
    >
      <PortalHomeContent homeSections={previewHomeSections} config={previewConfig} {...(tenantSlug !== undefined && { tenantSlug })} isPreview />
    </PortalLayout>
  )
}
